import React from 'react';
import {
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

export default function ServiceField({
  index,
  value,
  error,
  setValue,
  setError,
  remove,
  projectServices,
}) {
  const selectedService = projectServices.find(
    (service) => (service.id || service.service_id) === value.id
  );
  const serviceType = selectedService?.type?.toLowerCase() || '';

  // Get default target port based on service type
  const getDefaultTargetPort = (type) => {
    const normalizedType = type?.toLowerCase();
    if (normalizedType === 'https') return '80';
    if (normalizedType === 'http2') return '443';
    return '';
  };

  const handleServiceChange = (event) => {
    const newServiceId = event.target.value;
    const newService = projectServices.find(
      (service) => (service.id || service.service_id) === newServiceId
    );
    const newServiceType = newService?.type?.toLowerCase() || '';
    
    // Set default target port for HTTPS/HTTP2, preserve existing for TCP/UDP
    let newTargetPort = value.target_port;
    if (newServiceType === 'https' || newServiceType === 'http2') {
      newTargetPort = getDefaultTargetPort(newServiceType);
    } else if (newServiceType === 'tcp' || newServiceType === 'udp') {
      newTargetPort = value.target_port || '';
    } else {
      newTargetPort = undefined;
    }

    const newValue = {
      id: newServiceId,
      target_port: newTargetPort,
    };

    // Set the value - this will trigger validation in setServiceValue
    // The validation in setServiceValue will handle all error checking
    setValue(index, newValue);
  };

  const handleTargetPortChange = (event) => {
    const newTargetPort = event.target.value;
    const newValue = {
      ...value,
      target_port: newTargetPort,
    };

    // Validate target_port is not empty for tcp/udp
    if ((serviceType === 'tcp' || serviceType === 'udp') && !newTargetPort) {
      setError(index, 'Target port is required for TCP/UDP services');
    } else {
      setError(index, false);
    }

    setValue(index, newValue);
  };

  // Always show target port field, but enable/disable based on service type
  const isTargetPortEditable = serviceType === 'tcp' || serviceType === 'udp';
  const displayTargetPort = value.target_port || getDefaultTargetPort(serviceType);

  return (
    <Stack
      direction='row'
      justifyContent='flex-start'
      alignItems='center'
      spacing={1}
    >
      <Box sx={{ minWidth: '300px' }}>
        <FormControl fullWidth required>
          <InputLabel id={`service-${index}-label`}>Service</InputLabel>
          <Select
            labelId={`service-${index}-label`}
            id={`service-${index}`}
            value={value.id || ''}
            label='Service'
            onChange={handleServiceChange}
            aria-describedby={`service-${index}-helper-text`}
          >
            {projectServices.map((service) => (
              <MenuItem
                key={service.id || service.service_id}
                value={service.id || service.service_id}
              >
                {service.name}
              </MenuItem>
            ))}
          </Select>
          {error ? (
            <FormHelperText id={`service-${index}-helper-text`}>
              {error}
            </FormHelperText>
          ) : undefined}
        </FormControl>
      </Box>
      <Box sx={{ minWidth: '150px' }}>
        <TextField
          label='Target Port'
          id={`target-port-${index}`}
          value={displayTargetPort || ''}
          onChange={handleTargetPortChange}
          disabled={!isTargetPortEditable}
          error={Boolean(error && isTargetPortEditable)}
          variant='outlined'
          fullWidth
          required={isTargetPortEditable}
        />
      </Box>
      <IconButton
        onClick={() => {
          remove(index);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );
}

